import {
  CardDetailsTileList,
  type CardDetailsTileData,
} from "@-/dev.improve.haniverse.routes.nav.app._components.card.tile.link.details.list";
import type { CardTileType } from "@-/dev.improve.haniverse.routes.nav.app._components.card.tile";
import { formatCardDueLabel } from "@-/dev.improve.haniverse.routes.nav.app._components.card.tile.due-label";
import type { MorphemeDetailsTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import type { MorphemeStagesByGrapheme } from "@-/dev.improve.haniverse.lib.morpheme.stage";
import { useMemo, type ReactElement } from "react";
import type { CardContentKind } from "@-/dev.improve.haniverse.lib.cards.card-content";
import { userCardSelectionItem } from "@-/dev.improve.haniverse.routes.nav.app._context";

export type DeckCardTile = {
  cardType: CardTileType;
  contentKind: CardContentKind;
  due: string | null;
  isMastered?: boolean;
  /** The card's own tone-number reading if it is a pronunciation card; `null` for any other card. */
  pinyin: string | null;
  userCardId: string;
};

// Tapping a deck card opens its grapheme panels on the DECK-nested morpheme route, not the Browse
// one, so the panel is pushed onto the Decks stack and its back control returns to this deck view
// instead of stranding the user in Browse.
const DECK_MORPHEME_HREF = "/(nav)/(app)/decks/morpheme/[morpheme]";

export function DeckCardSectionDetailsTilesView({
  cards,
  deckCardTilesByCharacter,
  footer,
  isEditing,
  onRemoveCard,
  pendingDetailsFronts,
  stageByGrapheme,
}: {
  cards: MorphemeDetailsTileSlice[];
  deckCardTilesByCharacter?: Map<string, DeckCardTile[]>;
  footer?: ReactElement | null;
  isEditing: boolean;
  onRemoveCard?: (graphemeCard: MorphemeDetailsTileSlice) => void;
  /** Fronts whose `cards` entry is a provisional actor-only slice (details still loading). */
  pendingDetailsFronts?: ReadonlySet<string>;
  /** Stages derived from the deck's own actor rows; the list runs no stage query of its own. */
  stageByGrapheme: MorphemeStagesByGrapheme;
}) {
  // The deck renders cards, not morphemes: each morpheme expands into its
  // Meaning + Pronounce card tiles, each with its own FSRS due label.
  const cardDetailsTiles = useMemo<CardDetailsTileData[]>(
    () =>
      cards.flatMap((card) => {
        const tiles = deckCardTilesByCharacter?.get(card.front) ?? [];
        const isDetailsPending = pendingDetailsFronts?.has(card.front) ?? false;
        return tiles.map((tile) => ({
          cardType: tile.cardType,
          dueLabel: formatCardDueLabel(tile.due),
          graphemeCard: card,
          isDetailsPending,
          isMastered: tile.isMastered ?? false,
          pinyin: tile.pinyin,
          selectionItem: userCardSelectionItem({
            contentKind: tile.contentKind,
            front: card.front,
            userCardId: tile.userCardId,
          }),
        }));
      }),
    [cards, deckCardTilesByCharacter, pendingDetailsFronts],
  );

  return (
    <CardDetailsTileList
      href={DECK_MORPHEME_HREF}
      isEditing={isEditing}
      ListFooterComponent={footer}
      onRemoveCard={onRemoveCard}
      padding={4}
      scrollable={false}
      stageByGrapheme={stageByGrapheme}
      tiles={cardDetailsTiles}
    />
  );
}
